import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import AddonsHeader from "../../sections/subscriptionDashboard/AddonsHeader";
import AddonsTable from "../../sections/subscriptionDashboard/AddonsTable";
import EditAddonDialog from "../../sections/subscriptionDashboard/EditAddonDialog";
import {
  useAddons,
  useCreateAddon,
  useDeleteAddon,
  useToggleAddonStatus,
  useUpdateAddon,
} from "../../hooks/useSubscribtionDashboard";

const INITIAL_FORM_DATA = {
  name: "",
  description: "",
  privileges: [],
  priceMonthly: 0,
  priceAnnual: 0,
  allowedBillingMethods: ["Wallet", "Card"],
  isActive: true,
};

const normalizeAddonPayload = (formData) => ({
  name: String(formData.name || "").trim(),
  description: String(formData.description || "").trim(),
  privileges: Array.isArray(formData.privileges) ? formData.privileges : [],
  priceMonthly: Number(formData.priceMonthly) || 0,
  priceAnnual: Number(formData.priceAnnual) || 0,
  allowedBillingMethods: Array.isArray(formData.allowedBillingMethods)
    ? formData.allowedBillingMethods
    : [],
  isActive: Boolean(formData.isActive),
});

export default function AddonsManagement() {
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const offset = page * limit;

  const {
    data: addonsData,
    isLoading: addonsLoading,
    isError: addonsError,
    error: addonsErrorObj,
    refetch: addonsRefetch,
  } = useAddons({
    limit,
    offset,
  });

  const { mutate: createAddon, isPending: isCreatingAddon } = useCreateAddon();
  const { mutate: updateAddon, isPending: isUpdatingAddon } = useUpdateAddon();
  const { mutate: deleteAddon, isPending: isDeletingAddon } = useDeleteAddon();
  const { mutate: toggleAddonStatus, isPending: isTogglingStatus } =
    useToggleAddonStatus();

  const rawAddons = useMemo(() => {
    return (
      addonsData?.data?.data ??
      addonsData?.data ??
      addonsData?.addons ??
      addonsData ??
      []
    );
  }, [addonsData]);

  const addons = useMemo(() => {
    if (!Array.isArray(rawAddons)) return [];
    const hasServerPagination = Boolean(addonsData?.data?.meta?.total);
    return hasServerPagination
      ? rawAddons
      : rawAddons.slice(offset, offset + limit);
  }, [addonsData?.data?.meta?.total, limit, offset, rawAddons]);

  const totalCount = useMemo(() => {
    return addonsData?.data?.meta?.total ?? rawAddons.length ?? 0;
  }, [addonsData?.data?.meta?.total, rawAddons.length]);

  useEffect(() => {
    if (!selectedAddon) return;

    setFormData({
      name: selectedAddon.name || "",
      description: selectedAddon.description || "",
      privileges: Array.isArray(selectedAddon.privileges)
        ? selectedAddon.privileges
        : [],
      priceMonthly: selectedAddon.priceMonthly ?? 0,
      priceAnnual: selectedAddon.priceAnnual ?? 0,
      allowedBillingMethods: Array.isArray(selectedAddon.allowedBillingMethods)
        ? selectedAddon.allowedBillingMethods
        : ["Wallet", "Card"],
      isActive:
        selectedAddon.isActive !== undefined ? selectedAddon.isActive : true,
    });
  }, [selectedAddon]);

  const handleCreateAddon = () => {
    setSelectedAddon(null);
    setEditMode(false);
    setFormData(INITIAL_FORM_DATA);
    setDialogOpen(true);
  };

  const handleEditAddon = (addon) => {
    setSelectedAddon(addon);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAddon(null);
    setEditMode(false);
    setFormData(INITIAL_FORM_DATA);
  };

  const handleSubmitAddon = () => {
    const payload = normalizeAddonPayload(formData);

    if (editMode && selectedAddon) {
      updateAddon(
        {
          ...payload,
          id: selectedAddon._id || selectedAddon.id,
        },
        {
          onSuccess: handleCloseDialog,
        },
      );
      return;
    }

    createAddon(payload, {
      onSuccess: handleCloseDialog,
    });
  };

  const handleDeleteAddon = (addon) => {
    deleteAddon(addon._id || addon.id, {
      onSuccess: () => {
        const nextCount = Math.max(totalCount - 1, 0);
        const lastPage = Math.max(Math.ceil(nextCount / limit) - 1, 0);
        if (page > lastPage) {
          setPage(lastPage);
        }
      },
    });
  };

  const handleToggleAddonStatus = (addon, nextStatus) => {
    toggleAddonStatus({
      addonId: addon._id || addon.id,
      isActive: nextStatus,
    });
  };

  return (
    <Box>
      <AddonsHeader handleCreateAddon={handleCreateAddon} />

      <AddonsTable
        addons={addons}
        addonsLoading={addonsLoading}
        addonsError={addonsError}
        addonsErrorObj={addonsErrorObj}
        addonsRefetch={addonsRefetch}
        page={page}
        limit={limit}
        totalCount={totalCount}
        onPageChange={(newPage) => setPage(newPage)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(0);
        }}
        onEditAddon={handleEditAddon}
        onDeleteAddon={handleDeleteAddon}
        onToggleStatus={handleToggleAddonStatus}
        isTogglingStatus={isTogglingStatus}
        isDeletingAddon={isDeletingAddon}
      />

      <EditAddonDialog
        open={dialogOpen}
        editMode={editMode}
        formData={formData}
        setFormData={setFormData}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitAddon}
        isPending={isCreatingAddon || isUpdatingAddon}
      />
    </Box>
  );
}
